import React, { useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import ProjectSummaryDetails from '../ProjectSummaryDetails/ProjectSummaryDetails';
import './ResourceDashboardView.css';

const COLORS = {
  'Billable': '#0088FE',
  'Non-Billable': '#00C49F', 
  'Shadow': '#FFBB28',
  'billable': '#0088FE',
  'non-billable': '#00C49F',
  'shadow': '#FFBB28'
};

const ResourceDashboardView = ({ 
  resourceCountData, 
  billStatusSummary, 
  projectSummary,
  projectAllocationData // Added for the new ProjectSummaryDetails component
}) => {
  const [viewMode, setViewMode] = useState('tabular'); // 'tabular' or 'chart'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Add data validation and debugging
  console.log('ResourceDashboardView received data:', {
    resourceCountData: resourceCountData?.length || 0,
    billStatusSummary: billStatusSummary?.length || 0,
    projectSummary: projectSummary?.length || 0,
    projectAllocationData: projectAllocationData?.data?.length || 0,
    sampleResourceData: resourceCountData?.[0],
    sampleBillStatus: billStatusSummary?.[0],
    sampleProject: projectSummary?.[0],
    sampleAllocationData: projectAllocationData?.data?.[0]
  });

  const safeResourceCountData = Array.isArray(resourceCountData) ? resourceCountData : [];
  const safeBillStatusSummary = Array.isArray(billStatusSummary) ? billStatusSummary : [];
  const safeProjectSummary = Array.isArray(projectSummary) ? projectSummary : [];

  // Sorting functions
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️'; // Default unsorted icon
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Prepare processed data with computed values using useMemo for performance
  const processedProjectData = useMemo(() => {
    return safeResourceCountData.map((project, index) => {
      // Get total resources from processed data
      const totalResources = project.totalResources || project.TotalResources || 0;
      
      // Get totalBillableCount from processed data (this comes from the allowedCount/target count)
      const totalBillableCount = project.totalBillableCount || 0;
      
      // Debug logging for the first few projects
      console.log(`Project ${project.project}: Billable=${project.Billable}, TotalBillableCount=${totalBillableCount}, TotalResources=${totalResources}`);

      if (index < 3) {
      }
      
      // Calculate variance using new formula: (Total Resources - Total Billable count) / Total Billable count * 100
      let variance = 0;
      let varianceDisplay = '0.00%';
      
      if (totalBillableCount > 0) {
        variance = ((totalResources - totalBillableCount) / totalBillableCount) * 100;
        varianceDisplay = `${variance > 0 ? '+' : ''}${variance.toFixed(2)}%`;
      } else if (totalResources > 0) {
        // If no billable count but there are total resources, show as infinite variance
        varianceDisplay = 'Not Defined';
        variance = 999; // Set high value for styling purposes
      }

      return {
        ...project,
        index,
        projectName: project.project || 'Unknown',
        billableCount: project.Billable || 0,
        nonBillableCount: project['Non-Billable'] || 0,
        shadowCount: project.Shadow || 0,
        totalResources,
        totalBillableCount,
        variance,
        varianceDisplay
      };
    });
  }, [safeResourceCountData, projectAllocationData]);

  // Apply sorting using useMemo to ensure proper re-computation
  const sortedProjectData = useMemo(() => {
    if (!sortConfig.key || processedProjectData.length === 0) {
      return processedProjectData;
    }

    const sortedData = [...processedProjectData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Handle numeric comparisons
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string comparisons
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sortedData;
  }, [processedProjectData, sortConfig]);

  return (
    <div className="resource-dashboard-view">
      {/* View Mode Toggle */}
      <div className="resource-view-toggle-section">
        <div className="resource-view-toggle-container">
          <span className="resource-view-label">📋 Tabular View</span>
          <div className="resource-toggle-switch">
            <input
              type="checkbox"
              id="resourceViewToggle"
              className="resource-toggle-input"
              checked={viewMode === 'chart'}
              onChange={(e) => setViewMode(e.target.checked ? 'chart' : 'tabular')}
            />
            <label htmlFor="resourceViewToggle" className="resource-toggle-label">
              <span className="resource-toggle-slider"></span>
            </label>
          </div>
          <span className="resource-view-label">📊 Chart View</span>
        </div>
      </div>

      {/* Project Summary KPIs - Always at Top */}
      <div className="chart-section full-width">
        <h2>📈 Project Summary KPIs</h2>
        {safeProjectSummary.length > 0 ? (
          <div className="metrics-grid" style={{marginBottom: '2rem'}}>
            <div className="metric-card billable">
              <h3>Total Projects</h3>
              <div className="metric-value">{safeProjectSummary.length}</div>
              <div className="metric-secondary">Active Projects</div>
            </div>
            <div className="metric-card">
              <h3>Total Billable Resources</h3>
              <div className="metric-value">
                {safeProjectSummary.reduce((sum, project) => sum + (project.billableCount || 0), 0)}
              </div>
              <div className="metric-secondary">Across All Projects</div>
            </div>
            <div className="metric-card">
              <h3>Average Billable Ratio</h3>
              <div className="metric-value">
                {safeProjectSummary.length > 0 
                  ? Math.round(safeProjectSummary.reduce((sum, project) => sum + parseFloat(project.billableRatio || 0), 0) / safeProjectSummary.length)
                  : 0}%
              </div>
              <div className="metric-secondary">Per Project</div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <h3>📊 No data available</h3>
            <p>Please ensure the data.xlsx file is properly loaded.</p>
          </div>
        )}
      </div>

      {/* Conditional Rendering Based on View Mode */}
      {viewMode === 'tabular' ? (
        /* Tabular View - Only Tables */
        <div className="tabular-only-view">
          {/* Bill Status Summary Table Only */}
          <div className="chart-section">
            <h2>🥧 Bill Status Summary (All Resources)</h2>
            {safeBillStatusSummary.length > 0 ? (
              <div className="bill-status-summary">
                <div className="bill-status-tiles">
                  {safeBillStatusSummary.map((summary, index) => (
                    <div key={index} className="bill-status-tile">
                      <div className="tile-header">
                        <span 
                          className="status-badge"
                          style={{backgroundColor: COLORS[summary.name] || COLORS[summary.name?.toLowerCase()] || '#ccc'}}
                        >
                          {summary.name}
                        </span>
                      </div>
                      <div className="tile-content">
                        <div className="count-value">{summary.count}</div>
                        <div className="count-label">Count</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <h3>📊 No data available</h3>
                <p>loading...</p>
              </div>
            )}
          </div>

          {/* Enhanced Project Summary Details with Resource Mix & Profitability Analysis */}
          <div className="chart-section full-width">
            {projectAllocationData && projectAllocationData.data ? (
              <ProjectSummaryDetails projectAllocationData={projectAllocationData} />
            ) : (
              <div className="fallback-project-summary">
                <h2>📈 Project Summary Details</h2>
                {safeProjectSummary.length > 0 ? (
                  <div className="project-summary-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Billable Count</th>
                          <th>Allowed Count</th>
                          <th>Count Deviation (%)</th>
                          <th>Non-Billable</th>
                          <th>Shadow</th>
                          <th>Total Resources</th>
                          <th>Billable Ratio (%)</th>
                          <th>Current Allocation (%)</th>
                          <th>Max Allocation (%)</th>
                          <th>Allocation Deviation (%)</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeProjectSummary.map((project, index) => (
                          <tr key={index} className={`project-row ${project.status}`}>
                            <td className="project-name">{project.project || 'Unknown'}</td>
                            <td className="billable-count">{project.billableCount || 0}</td>
                            <td className="allowed-count">{project.allowedCount || 0}</td>
                            <td className={`count-deviation ${parseFloat(project.countDeviation || 0) > 20 ? 'critical' : parseFloat(project.countDeviation || 0) > 10 ? 'warning' : 'normal'}`}>
                              {(project.countDeviation || 0) > 0 ? '+' : ''}{project.countDeviation || 0}%
                            </td>
                            <td className="nonbillable-count">{project.nonBillableCount || 0}</td>
                            <td className="shadow-count">{project.shadowCount || 0}</td>
                            <td className="total-count">{project.totalCount || 0}</td>
                            <td className="billable-ratio">{project.billableRatio || 0}%</td>
                            <td className="current-allocation">{project.currentAllocation || 0}%</td>
                            <td className="max-allocation">{project.maxAllocation || 0}%</td>
                            <td className={`allocation-deviation ${parseFloat(project.allocationDeviation || 0) > 10 ? 'critical' : parseFloat(project.allocationDeviation || 0) > 5 ? 'warning' : 'normal'}`}>
                              {(project.allocationDeviation || 0) > 0 ? '+' : ''}{project.allocationDeviation || 0}%
                            </td>
                            <td>
                              <span className={`status-badge ${project.status}`}>
                                {project.status?.toUpperCase() || 'UNKNOWN'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <h3>📊 No data available</h3>
                    <p>Please ensure the data.xlsx file is properly loaded or provide project allocation data for enhanced analysis.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resource Counts Table Only - Moved to Bottom */}
          <div className="chart-section full-width">
            <h2>📊 Project-Level Billable Count & Allocation Overview</h2>
            {sortedProjectData.length > 0 ? (
              <div className="resource-counts-table">
                <h3>Detailed Project Data</h3>
                <table>
                  <thead>
                    <tr>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('projectName')}
                        title="Click to sort by Project Name"
                      >
                        Project Name {getSortIcon('projectName')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('billableCount')}
                        title="Click to sort by Billable count"
                      >
                        Billable {getSortIcon('billableCount')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('nonBillableCount')}
                        title="Click to sort by Non-Billable count"
                      >
                        Non-Billable {getSortIcon('nonBillableCount')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('shadowCount')}
                        title="Click to sort by Shadow count"
                      >
                        Shadow {getSortIcon('shadowCount')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('totalResources')}
                        title="Click to sort by Total Resources"
                      >
                        Total Resources {getSortIcon('totalResources')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('totalBillableCount')}
                        title="Click to sort by Billable Count (SOW)"
                      >
                        Billable Count (SOW) {getSortIcon('totalBillableCount')}
                      </th>
                      <th 
                        className="sortable-header" 
                        onClick={() => handleSort('variance')}
                        title="Click to sort by Variance %"
                      >
                        Variance % {getSortIcon('variance')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProjectData.map((project, index) => (
                      <tr key={project.index || index}>
                        <td className="project-name">{project.projectName}</td>
                        <td className="billable-count">{project.billableCount}</td>
                        <td className="nonbillable-count">{project.nonBillableCount}</td>
                        <td className="shadow-count">{project.shadowCount}</td>
                        <td className="total-resources">{project.totalResources}</td>
                        <td className="total-billable-count">{project.totalBillableCount}</td>
                        <td className={`variance ${project.varianceDisplay === 'Not Defined' ? 'variance-not-defined' : project.variance > 0 ? 'positive' : project.variance < 0 ? 'negative' : 'neutral'}`}>
                          {project.varianceDisplay}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <h3>📊 No data available</h3>
                <p>Please ensure the data.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Chart View - Reorganized Layout with Charts */
        <>
          {/* Bill Status Summary Charts */}
          <div className="chart-section">
            <h2>🥧 Bill Status Summary</h2>
            {safeBillStatusSummary.length > 0 ? (
              <>
                {/* Pie Chart for Bill Status Distribution */}
                <div className="chart-container" style={{marginBottom: '2rem'}}>
                  <h3>Resource Count Distribution by Bill Status</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={safeBillStatusSummary}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, count, percent }) => `${name}: ${count} (${(percent * 100).toFixed(1)}%)`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {safeBillStatusSummary.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS[entry.name?.toLowerCase()] || '#8884d8'} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [value, 'Count']}
                        labelFormatter={(label) => `Status: ${label}`}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <h3>📊 No data available</h3>
                <p>Please ensure the data.xlsx file is properly loaded.</p>
              </div>
            )}
          </div>

          {/* Project-Level Billable Count & Allocation Overview - Moved After Project Summary */}
          <div className="chart-section full-width">
            <h2>📊 Project-Level Billable Count & Allocation Overview</h2>
            {safeResourceCountData.length > 0 ? (
              <>
                {/* Bar Chart for Resource Counts */}
                <div className="chart-container" style={{marginBottom: '2rem'}}>
                  <h3>Resource Count Distribution by Project</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={safeResourceCountData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="project" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        interval={0}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [value, name]}
                        labelFormatter={(label) => `Project: ${label}`}
                      />
                      <Legend verticalAlign="top" wrapperStyle={{ top: 12, fontSize: 10 }}/>
                      <Bar dataKey="Billable" fill={COLORS.Billable} name="Billable Resources" />
                      <Bar dataKey="Non-Billable" fill={COLORS['Non-Billable']} name="Non-Billable Resources" />
                      <Bar dataKey="Shadow" fill={COLORS.Shadow} name="Shadow Resources" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Allocation Overview Chart */}
                <div className="chart-container" style={{marginBottom: '2rem'}}>
                  <h3>Allocation Percentage by Project</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={safeResourceCountData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="project" 
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                        interval={0}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [`${value}%`, name]}
                        labelFormatter={(label) => `Project: ${label}`}
                      />
                      <Legend  verticalAlign='top' wrapperStyle={{ top: 12, fontSize: 10 }} />
                      <Bar dataKey="billablePercentage" fill="#4CAF50" name="Billable Percentage" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <h3>📊 No data available</h3>
                <p>Please ensure the data.xlsx file is properly loaded.</p>
              </div>
            )}
      </div>
        </>
      )}
    </div>
  );
};

export default ResourceDashboardView; 